// Game catalogue with per-level round generators.
// 8 worlds — each with ONE parent-facing learning objective (Phase 1a).
// ORDER IS THE JOURNEY. PLAYABLE_REGIONS derives world 1, 2, 3… straight from
// the key order here, and starsNeeded() gates on a game's index inside its
// world — so reordering this object reorders the whole game.
//
// Shapes leads because a shape sorter is a toy a 1–2 year old already plays
// with, while COLOUR NAMING typically only firms up around age 3. Matching a
// circle to a circle long precedes knowing the word "bulat". Animals follow as
// the most recognisable category at 2. Colours and sounds move behind Pro.
//
// `free` must move with position: order alone would leave World 1 locked.
export const WORLDS = {
  shapes: { id: 'shapes', nameKey: 'shapes', bg: '/assets/backgrounds/bg_shapes.webp', music: 'theme_garden', greet: 'world_shapes', free: true, skill: 'shapes',
    objective: { ms: 'Dunia ini bantu anak kenal bentuk asas.', en: 'This world helps your child recognise basic shapes.' },
    witness: { ms: 'Anak mula nampak bentuk — bulat, segi empat, tiga segi.', en: 'Your child starts noticing shapes — circles, squares, triangles.' } },
  animals: { id: 'animals', nameKey: 'animals', bg: '/assets/backgrounds/bg_animals.webp', music: 'theme_forest', greet: 'world_animals', free: true, skill: 'animals',
    objective: { ms: 'Dunia ini bantu anak kenal nama dan bunyi haiwan.', en: 'This world helps your child learn animal names and sounds.' },
    witness: { ms: 'Anak boleh namakan haiwan dan tiru bunyinya.', en: 'Your child can name animals and copy their sounds.' } },
  garden: { id: 'garden', nameKey: 'garden', bg: '/assets/backgrounds/bg_garden.webp', music: 'theme_garden', greet: 'world_garden', skill: 'colours',
    objective: { ms: 'Dunia ini bantu anak kenal dan padankan warna.', en: 'This world helps your child recognise and match colours.' },
    witness: { ms: 'Selepas dunia ini, anak boleh tunjuk benda merah bila awak minta.', en: 'After this world, your child can point to something red when you ask.' } },
  forest: { id: 'forest', nameKey: 'forest', bg: '/assets/backgrounds/bg_forest.webp', music: 'theme_forest', greet: 'world_forest', skill: 'listening',
    objective: { ms: 'Dunia ini latih anak mendengar dan mengecam bunyi.', en: 'This world trains your child to hear and identify sounds.' },
    witness: { ms: 'Anak akan pandang bila dengar bunyi kucing atau loceng.', en: 'Your child will look up when they hear a cat or a bell.' } },
  island: { id: 'island', nameKey: 'island', bg: '/assets/backgrounds/bg_island.webp', music: 'theme_island', greet: 'world_island', skill: 'memory',
    objective: { ms: 'Dunia ini kuatkan ingatan dan tumpuan anak.', en: 'This world strengthens simple memory and focus.' },
    witness: { ms: 'Anak mula ingat apa yang baru hilang dari pandangan.', en: 'Your child starts remembering what just went out of sight.' } },
  size: { id: 'size', nameKey: 'size', bg: '/assets/backgrounds/bg_size.webp', music: 'theme_island', greet: 'world_size', skill: 'size',
    objective: { ms: 'Dunia ini ajar anak konsep besar-kecil.', en: 'This world teaches big and small.' },
    witness: { ms: 'Anak boleh pilih yang besar bila awak tanya ‘mana satu besar?’', en: 'Your child can pick the big one when you ask which is bigger.' } },
  move: { id: 'move', nameKey: 'move', bg: '/assets/backgrounds/bg_move.webp', music: 'theme_garden', greet: 'world_move', skill: 'coordination',
    objective: { ms: 'Dunia ini latih koordinasi tangan dan mata.', en: 'This world trains hand-eye coordination.' },
    witness: { ms: 'Jari anak jadi lebih tepat — dia kena apa yang dia tuju.', en: 'Your child aims more steadily — they hit what they reach for.' } },
  daily: { id: 'daily', nameKey: 'daily', bg: '/assets/backgrounds/bg_daily.webp', music: 'theme_island', greet: 'world_daily', skill: 'vocabulary',
    objective: { ms: 'Dunia ini ajar anak objek harian dan perkataan pertama.', en: 'This world teaches everyday objects and first words.' },
    witness: { ms: 'Anak mula namakan benda harian — cawan, kasut, pintu.', en: 'Your child starts naming everyday things — cup, shoe, door.' } },
};

export const COLOR_HEX = { red: '#FF5A5A', blue: '#45B6FE', yellow: '#FFD166', green: '#4ADE80', orange: '#FF9440', teal: '#3ED3E0', lime: '#A8E05A', gold: '#E6B84C' };
const COLORS = ['red', 'blue', 'yellow', 'green'];
// [base colour with a voice line, close shade distractor] — for level-3 harder discrimination
const SHADE_SETS = [['red', 'orange'], ['blue', 'teal'], ['green', 'lime'], ['yellow', 'gold']];
const ball = (c, id) => ({ kind: 'ball', color: c, id: id || c });
const flower = (c, id) => ({ kind: 'flower', color: c, id: id || c });
const shape = (sh, c, id, size) => ({ kind: 'shape', shape: sh, color: c, id, size });
const img = (n) => ({ kind: 'img', id: n, src: `/assets/objects/obj_${n}.webp`, word: `word_${n}` });

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
const pick = (a, n) => shuffle(a).slice(0, n);

const ANIMALS = ['cat', 'dog', 'bird', 'cow', 'duck'];
const ANIMAL_SND = { cat: 'snd_cat', dog: 'snd_dog', bird: 'snd_bird', cow: 'snd_cow', duck: 'snd_duck' };
const OBJECTS = ['car', 'rain', 'bell', 'drum', 'water', 'door'];
const OBJECT_SND = { car: 'snd_car', rain: 'snd_rain', bell: 'snd_bell', drum: 'snd_drum', water: 'snd_water', door: 'snd_door' };
const OBJECT_IMG = { water: 'cup' };
const VOCAB = ['cat', 'bird', 'ball', 'cup', 'spoon', 'car', 'shoe', 'door', 'pillow'];
const ACTIONS = [
  { id: 'act_head', icon: '🙆' }, { id: 'act_clap', icon: '👏' }, { id: 'act_knees', icon: '🧎' },
  { id: 'act_wave', icon: '👋' }, { id: 'act_stand', icon: '🧍' }, { id: 'act_sit', icon: '🪑' },
];

function tapColourRound(level) {
  const cs = pick(COLORS, level === 1 ? 2 : 3);
  const correct = cs[0];
  const make = level === 3 ? flower : ball;
  return { type: 'tap-choice', voice: `tap_${correct}`, choices: shuffle(cs.map((c) => make(c))), correct };
}

function colourMatchRound(level) {
  let cs;
  if (level === 3) {
    const [a, b] = pick(SHADE_SETS, 1)[0];
    cs = [a, b, pick(COLORS.filter((c) => c !== a), 1)[0]];
  } else cs = pick(COLORS, level === 1 ? 2 : 3);
  return { type: 'tap-choice', voice: 'match_colour', target: flower(cs[0], 'target'),
    choices: shuffle(cs.map((c) => ({ kind: 'basket', color: c, id: c }))), correct: cs[0] };
}

function colourFeedRound(level) {
  let cs;
  if (level === 3) {
    const [a, b] = pick(SHADE_SETS, 1)[0];
    cs = [a, b, pick(COLORS.filter((c) => c !== a), 1)[0]];
  } else cs = pick(COLORS, level === 1 ? 2 : 3);
  // The visual is fruit, so the line must say fruit. `tap_${c}` says "ball" —
  // it belongs to tap_colour. Falls back to it only until feed_* is recorded.
  return { type: 'tap-choice', voice: `feed_${cs[0]}`, voiceAlt: `tap_${cs[0]}`,
    choices: shuffle(cs.map((c) => ({ kind: 'fruit', color: c, id: c }))), correct: cs[0] };
}

function basketsRound(level) {
  const n = level === 1 ? 2 : 3;
  const items = pick(COLORS, n);
  const targets = level === 3 ? pick(COLORS, 4) : items;
  return { type: 'category-sort', voice: 'basket',
    items: items.map((c) => ({ item: ball(c), target: c })),
    targets: (level === 3 ? [...new Set([...items, ...targets])].slice(0, 4) : items).map((c) => ({ id: c, visual: { kind: 'basket', color: c, id: `b-${c}` } })) };
}

function oddRound(level) {
  const n = level === 1 ? 3 : 4;
  const [main, diff] = pick(COLORS, 2);
  const choices = Array.from({ length: n - 1 }, (_, i) => flower(main, `m${i}`));
  choices.push(flower(diff, 'odd'));
  return { type: 'tap-choice', voice: 'odd', choices: shuffle(choices), correct: 'odd' };
}

function animalSoundRound(level) {
  const cs = pick(ANIMALS, level === 1 ? 2 : 3);
  return { type: 'sound-match', voice: 'sound_match', sound: ANIMAL_SND[cs[0]], choices: shuffle(cs.map(img)), correct: cs[0] };
}

function objectSoundRound(level) {
  const cs = pick(OBJECTS, level === 1 ? 2 : 3);
  return { type: 'sound-match', voice: 'sound_match', sound: OBJECT_SND[cs[0]],
    choices: shuffle(cs.map((o) => ({ ...img(OBJECT_IMG[o] || o), id: o }))), correct: cs[0] };
}

function disappearedRound(level) {
  const n = level === 1 ? 2 : 3;
  const pool = ['ball', 'cup', 'spoon', 'car', 'shoe', 'duck'];
  return { type: 'what-disappeared', voice: 'mem_disappear', items: pick(pool, n).map(img), showMs: level === 3 ? 1800 : 2500 };
}

function trailRound(level, itemPool) {
  const len = level === 1 ? 2 : 3;
  let seq = pick(itemPool, Math.min(len, itemPool.length));
  if (level === 3 && seq.length >= 2) seq = [seq[0], seq[1], seq[0]];
  const stones = pick(itemPool, Math.min(4, itemPool.length));
  seq.forEach((s) => { if (!stones.includes(s)) stones[stones.length - 1] = s; });
  return { seq, stones: shuffle([...new Set([...stones, ...seq])]) };
}

function colourTrailRound(level) {
  const { seq, stones } = trailRound(level, COLORS);
  return { type: 'sequence-tap', voice: 'trail', sequence: seq, choices: stones.map((c) => ({ kind: 'stone', color: c, id: c })) };
}

function copyPikoRound(level) {
  const pool = COLORS;
  const { seq, stones } = trailRound(level, pool);
  return { type: 'sequence-tap', voice: 'trail', sequence: seq, choices: stones.map((c) => ball(c)), piko: true };
}

function soundSeqRound(level) {
  const pool = ['bell', 'drum', 'water'];
  const len = 2;
  let seq = pick(pool, len);
  if (level === 3) seq = [seq[0], seq[1], seq[0]];
  const choices = pool.slice(0, level === 1 ? 2 : 3);
  seq.forEach((s) => { if (!choices.includes(s)) choices[choices.length - 1] = s; });
  return { type: 'sequence-tap', voice: 'sound_seq', sequence: seq, sounds: OBJECT_SND,
    choices: [...new Set(choices)].map((o) => ({ ...img(OBJECT_IMG[o] || o), id: o })) };
}

function patternRound(level) {
  const [a, b] = pick(COLORS, 2);
  const pat = level >= 2 ? [a, b, a, b, a] : [a, b, a];
  const answer = pat.length % 2 === 0 ? a : b;
  const nChoices = level === 3 ? 3 : 2;
  const others = pick(COLORS.filter((c) => c !== answer), nChoices - 1);
  return { type: 'pattern-choice', voice: 'pattern', pattern: pat.map((c) => flower(c, `p${Math.random()}`)),
    choices: shuffle([answer, ...others].map((c) => flower(c))), correct: answer };
}

function detectiveRound(level) {
  const shapes = ['circle', 'star', 'square'];
  if (level === 1) {
    const cs = pick(COLORS, 3);
    return { type: 'tap-choice', voice: 'detective', target: shape('circle', cs[0], 'target'),
      choices: shuffle(cs.map((c) => shape('circle', c, c))), correct: cs[0] };
  }
  if (level === 2) {
    const ss = pick(shapes, 3);
    const c = pick(COLORS, 1)[0];
    return { type: 'tap-choice', voice: 'detective', target: shape(ss[0], c, 'target'),
      choices: shuffle(ss.map((s) => shape(s, c, s))), correct: ss[0] };
  }
  const combos = shuffle(shapes.flatMap((s) => COLORS.map((c) => ({ s, c })))).slice(0, 4);
  const t = combos[0];
  return { type: 'tap-choice', voice: 'detective', target: shape(t.s, t.c, 'target'),
    choices: shuffle(combos.map((x, i) => shape(x.s, x.c, `${x.s}-${x.c}`))), correct: `${t.s}-${t.c}` };
}

function listenRound(level) {
  const n = level === 1 ? 2 : level === 2 ? 3 : 4;
  const cs = pick(VOCAB, n);
  return { type: 'listen-choose', voice: 'listen_choose', word: `word_${cs[0]}`, choices: shuffle(cs.map(img)), correct: cs[0] };
}

function pikoSaysRound(level) {
  const acts = pick(ACTIONS, level === 1 ? 1 : 2);
  return { type: 'action-mission', voice: 'piko_says', actions: acts };
}

function twoStepRound() {
  return { type: 'action-mission', voice: 'two_step', twoStep: true };
}

function locationRound(level) {
  return { type: 'remember-location', voice: 'mem_where', item: img(pick(['ball', 'shoe', 'duck'], 1)[0]),
    cups: level >= 2 ? 3 : 2, swap: level === 3 };
}

function pairRound(level) {
  const n = level === 1 ? 2 : 3;
  return { type: 'find-pair', voice: 'mem_pair', pairs: pick(['cat', 'ball', 'duck', 'car', 'bird'], n).map(img) };
}

function bagRound(level) {
  const pool = ['cup', 'bird', 'car', 'shoe', 'ball', 'spoon'];
  const inside = pick(pool, level === 3 ? 3 : 2);
  const distractors = pick(pool.filter((p) => !inside.includes(p)), level === 1 ? 1 : 2);
  return { type: 'piko-bag', voice: 'mem_bag', inside: inside.map(img), distractors: distractors.map(img) };
}

function sortRound(level) {
  const cats = level === 3
    ? { moves: ['car', 'duck', 'bird'], still: ['cup', 'pillow', 'door'] }
    : { animals: ['cat', 'dog', 'bird', 'duck'], toys: ['ball', 'car'] };
  const keys = Object.keys(cats);
  const nPer = level === 1 ? 2 : 3;
  const items = keys.flatMap((k) => pick(cats[k], Math.min(nPer, cats[k].length)).map((o) => ({ item: img(o), target: k })));
  return { type: 'category-sort', voice: 'sort',
    items: shuffle(items).slice(0, level === 1 ? 4 : 6),
    targets: keys.map((k, i) => ({ id: k, visual: { kind: 'zone', color: i === 0 ? 'green' : 'blue', id: k, icon: k } })) };
}

function bigSmallRound(level) {
  const target = Math.random() < 0.5 ? 'big' : 'small';
  const sh = pick(['circle', 'star', 'square'], 1)[0];
  const n = level === 3 ? 3 : 2;
  const c = pick(COLORS, 1)[0];
  const choices = Array.from({ length: n }, (_, i) => {
    const big = target === 'big' ? i === 0 : i !== 0;
    return shape(sh, c, `s${i}`, big ? undefined : 'small');
  });
  return { type: 'tap-choice', voice: `tap_${target}`, choices: shuffle(choices), correct: 's0' };
}

function patternProRound(level) {
  const [a, b, c] = pick(COLORS, 3);
  const unit = level === 1 ? [a, a, b] : level === 2 ? [a, b, b] : [a, b, c];
  const full = [...unit, ...unit];
  const answer = full[full.length - 1];
  const others = pick(COLORS.filter((x) => x !== answer), 2);
  return { type: 'pattern-choice', voice: 'pattern', pattern: full.slice(0, -1).map((x) => flower(x, `p${Math.random()}`)),
    choices: shuffle([answer, ...others].map((x) => flower(x))), correct: answer };
}

function soundMasterRound(level) {
  const pool = [...ANIMALS, ...OBJECTS];
  const n = level === 1 ? 3 : level === 2 ? 4 : 5;
  const cs = pick(pool, n);
  const snd = { ...ANIMAL_SND, ...OBJECT_SND };
  return { type: 'sound-match', voice: 'sound_match', sound: snd[cs[0]],
    choices: shuffle(cs.map((o) => ({ ...img(OBJECT_IMG[o] || o), id: o }))), correct: cs[0] };
}

function echoSeqRound(level) {
  const pool = ['bell', 'drum', 'water', 'rain'];
  let seq = pick(pool, level === 1 ? 2 : 3);
  if (level === 3) seq = [...seq, seq[0]];
  const choices = [...new Set([...seq, ...pick(pool, 4)])].slice(0, 4);
  return { type: 'sequence-tap', voice: 'sound_seq', sequence: seq, sounds: OBJECT_SND,
    choices: choices.map((o) => ({ ...img(OBJECT_IMG[o] || o), id: o })) };
}

function superPairRound(level) {
  const n = level === 1 ? 3 : 4;
  return { type: 'find-pair', voice: 'mem_pair', pairs: pick(['cat', 'ball', 'duck', 'car', 'bird', 'shoe', 'cup', 'drum'], n).map(img) };
}

function megaTrailRound(level) {
  const len = level === 1 ? 3 : level === 2 ? 3 : 4;
  const seq = Array.from({ length: len }, () => COLORS[Math.floor(Math.random() * COLORS.length)]);
  return { type: 'sequence-tap', voice: 'trail', sequence: seq, choices: COLORS.map((c) => ({ kind: 'stone', color: c, id: c })) };
}

const SHAPES3 = ['circle', 'square', 'triangle'];

function loudSoftRound(level) {
  const drums = level === 3
    ? [{ id: 'loud', vol: 1 }, { id: 'soft', vol: 0.12 }, { id: 'mid', vol: 0.4 }]
    : [{ id: 'loud', vol: 1 }, { id: 'soft', vol: level === 1 ? 0.1 : 0.35 }];
  return { type: 'loud-soft', voice: 'tap_loud', drums: shuffle(drums), correct: 'loud' };
}

function tapShapeRound(level) {
  const ss = pick(SHAPES3, level === 1 ? 2 : 3);
  const cs = level === 3 ? ss.map(() => 'blue') : pick(COLORS, 3);
  return { type: 'tap-choice', voice: `tap_${ss[0]}`, choices: shuffle(ss.map((s, i) => shape(s, cs[i], s))), correct: ss[0] };
}

function shapeHoleRound(level) {
  const ss = pick(SHAPES3, level === 1 ? 2 : 3);
  const c = pick(COLORS, 1)[0];
  return { type: 'tap-choice', voice: 'shape_hole', target: { kind: 'hole', shape: ss[0], id: 'hole' },
    choices: shuffle(ss.map((s) => shape(s, c, s))), correct: ss[0] };
}

function shapeFindRound(level) {
  const sets = { 1: ['ball', 'door'], 2: ['ball', 'door', 'shoe'], 3: ['ball', 'cup', 'door'] };
  return { type: 'tap-choice', voice: 'tap_circle', choices: shuffle(sets[level].map(img)), correct: 'ball' };
}

function manyFewRound(level) {
  const [a, b] = { 1: [3, 1], 2: [4, 2], 3: [4, 3] }[level];
  const c = pick(COLORS, 1)[0];
  return { type: 'tap-choice', voice: 'tap_many',
    choices: shuffle([{ kind: 'plate', count: a, color: c, id: 'many' }, { kind: 'plate', count: b, color: c, id: 'few' }]), correct: 'many' };
}

function tallShortRound(level) {
  const hs = level === 1 ? [1, 0.55] : level === 2 ? [1, 0.72] : [1, 0.62, 0.8];
  return { type: 'tap-choice', voice: 'tap_tall',
    choices: shuffle(hs.map((h, i) => ({ kind: 'tree', h, id: i === 0 ? 'tall' : `t${i}` }))), correct: 'tall' };
}

function animalNameRound(level) {
  let cs;
  if (level === 3) cs = [...shuffle(['cat', 'dog']), pick(['cow', 'duck', 'bird'], 1)[0]];
  else cs = pick(ANIMALS, level === 1 ? 2 : 3);
  return { type: 'listen-choose', voice: 'listen_choose', word: `word_${cs[0]}`, choices: shuffle(cs.map(img)), correct: cs[0] };
}

function animalHomeRound(level) {
  const fish = Math.random() < 0.5;
  const correct = fish ? 'pond' : 'nest';
  const others = fish ? ['tree', 'door'] : ['pond', 'door'];
  const n = level === 1 ? 1 : 2;
  return { type: 'tap-choice', voice: fish ? 'home_fish' : 'home_bird', target: img(fish ? 'fish' : 'bird'),
    choices: shuffle([img(correct), ...others.slice(0, n).map(img)]), correct };
}

function animalBabyRound(level) {
  const d = level === 1 ? ['dog'] : level === 2 ? ['dog', 'cow'] : ['duck', 'bird'];
  return { type: 'tap-choice', voice: 'baby_hen', target: img('hen'),
    choices: shuffle([img('chick'), ...d.map(img)]), correct: 'chick' };
}

const motionRound = (mode) => (level) => ({
  type: 'motion-tap', mode, level,
  voice: mode === 'bubble' ? 'pop_bubble' : mode === 'star' ? 'catch_star' : 'tap_dots',
});

// Co-play: Piko sets the round up, then stays quiet so the PARENT becomes the
// voice. `coplay` tells TapChoice to skip the spoken prompt and show the word
// for an adult to read aloud instead. Not a new component — one flag.
const COPLAY_WORDS = {
  cup:    ['cawan', 'cup'],
  shoe:   ['kasut', 'shoe'],
  ball:   ['bola', 'ball'],
  spoon:  ['sudu', 'spoon'],
  door:   ['pintu', 'door'],
  pillow: ['bantal', 'pillow'],
};

function playTogetherRound(level) {
  const ids = pick(Object.keys(COPLAY_WORDS), level === 1 ? 2 : 3);
  const target = ids[0];
  return {
    type: 'tap-choice',
    coplay: true,
    say: { ms: COPLAY_WORDS[target][0], en: COPLAY_WORDS[target][1] },
    voice: `word_${target}`,
    choices: shuffle(ids.map(img)),
    correct: target,
  };
}

function objectNameRound(level) {
  let cs;
  if (level === 3) cs = [...shuffle(['cup', 'spoon']), 'ball'];
  else cs = pick(['cup', 'shoe', 'ball', 'spoon', 'door', 'pillow'], level === 1 ? 2 : 3);
  return { type: 'listen-choose', voice: 'listen_choose', word: `word_${cs[0]}`, choices: shuffle(cs.map(img)), correct: cs[0] };
}

const USE_SCENARIOS = [
  { voice: 'use_thirsty', correct: 'cup', others: ['shoe', 'ball'] },
  { voice: 'use_sleepy', correct: 'pillow', others: ['car', 'cup'] },
  { voice: 'use_walk', correct: 'shoe', others: ['spoon', 'pillow'] },
];
function objectUseRound(level) {
  const s = pick(USE_SCENARIOS, 1)[0];
  const n = level === 1 ? 1 : 2;
  return { type: 'tap-choice', voice: s.voice,
    choices: shuffle([img(s.correct), ...s.others.slice(0, n).map(img)]), correct: s.correct };
}

function bodyPartRound(level) {
  const ps = pick(['nose', 'eye', 'hand'], level === 1 ? 2 : 3);
  return { type: 'tap-choice', voice: `tap_${ps[0]}`,
    choices: shuffle(ps.map((p) => ({ kind: 'body', part: p, id: p }))), correct: ps[0] };
}

// ── New games (2026-07-28). Every one reuses an existing component. ──

// Tempo, not volume. Reuses the loud-soft component: `beats`+`gap` drive the
// rhythm and every tap plays that drum, so the child explores by touching
// instead of holding two sounds in memory — the fix loud_soft itself needed.
function fastSlowRound(level) {
  const fast = { id: 'fast', vol: 0.9, beats: 5, gap: 160 };
  const slow = { id: 'slow', vol: 0.9, beats: 2, gap: level === 1 ? 850 : 620 };
  const drums = level === 3 ? [fast, slow, { id: 'mid', vol: 0.9, beats: 3, gap: 400 }] : [fast, slow];
  return { type: 'loud-soft', voice: 'tap_fast', voiceAlt: 'tap_loud', drums: shuffle(drums), correct: 'fast' };
}

// Horizontal on purpose — 'tall' already owns the vertical axis in this world.
function longShortRound(level) {
  const ls = level === 1 ? [1, 0.45] : level === 2 ? [1, 0.62] : [1, 0.5, 0.75];
  return { type: 'tap-choice', voice: 'tap_long', voiceAlt: 'tap_tall',
    choices: shuffle(ls.map((l, i) => ({ kind: 'rope', len: l, color: i === 0 ? 'orange' : 'teal', id: i === 0 ? 'long' : `r${i}` }))),
    correct: 'long' };
}

// Matching by sight comes long before naming: a child pairs circle-to-circle
// well before they know the word "bulat". This sits ahead of tap_shape.
function shapeSameRound(level) {
  const ss = pick(SHAPES3, level === 1 ? 2 : 3);
  const cs = pick(COLORS, 3);
  return { type: 'tap-choice', voice: 'match_shape', voiceAlt: 'match_colour',
    target: { kind: 'shape', shape: ss[0], color: 'blue', id: 'target' },
    choices: shuffle(ss.map((s, i) => shape(s, cs[i], s))), correct: ss[0] };
}

// Face-UP cards. The step before `pair`, which hides them.
function samePictureRound(level) {
  const items = pick(['ball', 'duck', 'cat', 'cup', 'car'], level === 1 ? 2 : 3);
  const twin = items[0];
  return { type: 'tap-choice', voice: 'mem_pair',
    target: { ...img(twin), id: 'target' },
    choices: shuffle(items.map((o) => ({ ...img(o), id: o }))), correct: twin };
}

// No wrong answer at all. The safest possible first game in a world.
function touchAnimalRound(level) {
  const cs = pick(ANIMALS, level === 1 ? 2 : 3);
  return { type: 'tap-choice', voice: 'touch_animal', voiceAlt: 'listen_choose', allCorrect: true,
    choices: shuffle(cs.map((o) => ({ ...img(o), id: o }))), correct: cs[0] };
}

// Also no wrong answer — pure cause and effect.
function pikoPianoRound(level) {
  const keys = COLORS.slice(0, level === 1 ? 3 : 4);
  return { type: 'tap-choice', voice: 'piko_piano', voiceAlt: 'lets_play', allCorrect: true,
    choices: keys.map((c) => shape('square', c, c)), correct: keys[0] };
}

// ── Drag-and-drop batch (2026-07-29) ────────────────────────────────────────
// One component, `drag-drop`, serves all sixteen. EVERY ONE is finishable by
// TAP ALONE — tap the object, tap the destination, same reward. Drag is an
// enhancement. A release that misses floats home in silence and counts as
// nothing; only a release onto a genuinely wrong target is an attempt.
//
// `needed` exists because "choose the one that…" rounds show three items and
// want one. Items that must never be accepted carry target 'none'.
const dragItem = (visual, target) => ({ visual, target });
const PIKO = { kind: 'img', id: 'piko', src: '/assets/characters/piko_happy.webp' };

// D1 · Post the shape into its matching hole. The shape-sorter toy, on screen.
function shapePostRound(level) {
  const holes = pick(SHAPES3, level === 1 ? 2 : 3);
  const carried = level === 1 ? holes.slice(0, 1) : holes.slice(0, 2);
  const cs = pick(COLORS, 3);
  return { type: 'drag-drop', voice: 'post_shape', voiceAlt: 'shape_hole',
    items: shuffle(carried.map((s, i) => dragItem(shape(s, cs[i], `it-${s}`), s))),
    targets: shuffle(holes.map((s) => ({ id: s, visual: { kind: 'hole', shape: s, id: `hole-${s}` } }))) };
}

// D1 · Match a shape to its own silhouette. Form without colour.
function shapeShadowRound(level) {
  const ss = pick(SHAPES3, level === 1 ? 2 : 3);
  const carried = level === 3 ? ss.slice(0, 2) : ss.slice(0, 1);
  const cs = pick(COLORS, 3);
  return { type: 'drag-drop', voice: 'find_shadow', voiceAlt: 'match_shape',
    items: shuffle(carried.map((s, i) => dragItem(shape(s, cs[i], `it-${s}`), s))),
    targets: shuffle(ss.map((s) => ({ id: s, visual: { kind: 'shadow', id: `sh-${s}`, of: { kind: 'shape', shape: s } } }))) };
}

// D2 · No wrong answer at all — the safest possible first drag in the game.
function animalFeedRound(level) {
  const as = pick(ANIMALS, level === 1 ? 2 : 3);
  const cs = pick(['red', 'orange', 'lime'], level === 1 ? 1 : 2);
  return { type: 'drag-drop', allCorrect: true, needed: 1,
    voice: 'feed_animal', voiceAlt: 'touch_animal',
    items: cs.map((c, i) => dragItem({ kind: 'fruit', color: c, id: `fruit-${i}` }, as[0])),
    targets: shuffle(as.map((a) => ({ id: a, visual: { ...img(a), id: a } }))) };
}

// D2 · Where does it live? bird→nest, fish→pond.
function animalHomeDragRound(level) {
  const PAIRS = [['bird', 'nest'], ['fish', 'pond']];
  const carried = level >= 2 ? PAIRS : pick(PAIRS, 1);
  const homes = level === 3 ? ['nest', 'pond', 'tree'] : ['nest', 'pond'];
  // `home_bird` says "TEKAN rumahnya" — wrong modality for a drag game, and at
  // level 2+ the round carries a fish too. `sort` is modality-neutral.
  return { type: 'drag-drop', voice: 'take_home', voiceAlt: 'sort',
    items: shuffle(carried.map(([a, h]) => dragItem({ ...img(a), id: a }, h))),
    targets: shuffle(homes.map((h) => ({ id: h, visual: { ...img(h), id: h } }))) };
}

// D3 · One-to-one by colour.
function colourBasketRound(level) {
  const cs = pick(COLORS, level === 1 ? 2 : 3);
  const carried = level === 1 ? cs.slice(0, 1) : cs.slice(0, 2);
  // FRUIT, not balls. The recorded `basket` line says "Seret buah ke bakul yang
  // sama warna" — with balls on screen the game would be telling the child about
  // an object that is not there. Same class of bug as the line that once said
  // "tekan bola" over a screen of fruit.
  return { type: 'drag-drop', voice: 'basket',
    items: shuffle(carried.map((c) => dragItem({ kind: 'fruit', color: c, id: c }, c))),
    targets: shuffle(cs.map((c) => ({ id: c, visual: { kind: 'basket', color: c, id: `b-${c}` } }))) };
}

// D3 · Same idea, different objects — a blob of paint to the flower it belongs
// to. Colour matching without ever naming a colour.
function colourPaintRound(level) {
  const cs = pick(COLORS, level === 1 ? 2 : 3);
  const carried = level === 3 ? cs.slice(0, 2) : cs.slice(0, 1);
  // The flowers start GREY and take the colour of the paint dropped on them —
  // `paints` tells DragDrop to recolour the target on a hit. Without that this
  // was colour_basket with different pictures, and the cause-and-effect payoff
  // that makes a 2-year-old want to do it again was missing.
  return { type: 'drag-drop', voice: 'paint_flower', voiceAlt: 'match_colour', paints: true,
    items: shuffle(carried.map((c) => dragItem({ kind: 'paint', color: c, id: `paint-${c}` }, c))),
    targets: shuffle(cs.map((c) => ({ id: c, colour: c, visual: { kind: 'flower', color: 'grey', id: `f-${c}` } }))) };
}

// D4 · Hear it, then carry it. The match has to survive the movement.
function soundDragRound(level) {
  const as = pick(ANIMALS, level === 1 ? 2 : 3);
  const correct = as[0];
  return { type: 'drag-drop', needed: 1, voice: 'sound_drag', voiceAlt: 'sound_match',
    sound: ANIMAL_SND[correct], sounds: Object.fromEntries(as.map((a) => [a, ANIMAL_SND[a]])),
    items: shuffle(as.map((a) => dragItem({ ...img(a), id: a }, a === correct ? 'piko' : 'none'))),
    targets: [{ id: 'piko', visual: { kind: 'basket', color: 'yellow', id: 'piko-basket' } }] };
}

// D4 · No wrong answer — every instrument plays when it reaches Piko.
function musicMakerRound(level) {
  const insts = level === 1 ? ['drum', 'bell'] : ['drum', 'bell', 'rain'];
  return { type: 'drag-drop', allCorrect: true, needed: 1,
    voice: 'music_maker', voiceAlt: 'piko_piano',
    sounds: Object.fromEntries(insts.map((o) => [o, OBJECT_SND[o]])),
    items: insts.map((o) => dragItem({ ...img(OBJECT_IMG[o] || o), id: o }, 'piko')),
    targets: [{ id: 'piko', visual: PIKO }] };
}

// D5 · Shown, hidden, then fetched back. Memory plus a motor act.
function bringBackRound(level) {
  const cs = pick(['ball', 'cup', 'duck', 'car', 'shoe'], level === 1 ? 2 : 3);
  const correct = cs[0];
  return { type: 'drag-drop', needed: 1, voice: 'bring_back', voiceAlt: 'mem_pair',
    showFirst: true, showMs: 2600,
    // Hold the peek at 2600ms on every level. Level 3 used to cut it to 1800,
    // shortening the only moment that carries the information.
    showItem: { ...img(correct), id: `peek-${correct}` },
    items: shuffle(cs.map((o) => dragItem({ ...img(o), id: o }, o === correct ? 'piko' : 'none'))),
    targets: [{ id: 'piko', visual: PIKO }] };
}

// D5 · Level 1 asks for ONE thing. Two items in mind at once only from level 2.
function packBagRound(level) {
  const cs = pick(['cup', 'shoe', 'spoon', 'ball', 'duck'], level === 1 ? 2 : 3);
  const want = level === 1 ? 1 : 2;
  const wanted = cs.slice(0, want);
  // Piko NAMES what he wants — `names` plays the already-recorded word_* clips
  // after the prompt. Without this the child is punished for not guessing which
  // of three objects was silently chosen, which is a guessing game with a
  // wrong-answer path, not a memory game.
  // `mem_bag` is NOT usable even as a fallback: it asks what WAS in the bag.
  return { type: 'drag-drop', needed: want, voice: 'pack_bag', voiceAlt: 'sort',
    names: wanted.map((o) => `word_${o}`),
    items: shuffle(cs.map((o) => dragItem({ ...img(o), id: o }, wanted.includes(o) ? 'bag' : 'none'))),
    targets: [{ id: 'bag', visual: { kind: 'basket', color: 'blue', id: 'bag' } }] };
}

// D6 · Size made physical: the big one only fits the big hole.
function sizeNestRound(level) {
  const c = pick(COLORS, 1)[0];
  // 'small' on the BIG ball too: the draggable renders at 88px while the big
  // hole's aperture is only ~62px, so an unscaled ball was visibly larger than
  // the hole it is supposed to drop into — contradicting the one thing the game
  // asserts. Relative size between the two balls is what carries the question.
  const carried = level === 1
    ? [dragItem(shape('circle', c, 'big', 'small'), 'big')]
    : [dragItem(shape('circle', c, 'big', 'small'), 'big'), dragItem(shape('circle', c, 'small', 'tiny'), 'small')];
  // `tap_big` says "TEKAN yang besar" — wrong modality for a drag game.
  return { type: 'drag-drop', voice: 'big_hole', voiceAlt: 'sort',
    items: shuffle(carried),
    targets: [
      { id: 'big', visual: { kind: 'hole', shape: 'circle', id: 'hole-big' } },
      { id: 'small', visual: { kind: 'hole', shape: 'circle', id: 'hole-small', size: 'small' } },
    ] };
}

// D6 · The stacker toy. ORDER IS NOT ENFORCED — insisting on biggest-first
// would create a fail state, and seriation only firms at 2.5–3. The order lives
// in the spoken line; putting any ring on the pole is a success.
function stackRingRound(level) {
  const cs = pick(COLORS, 3);
  const n = level === 3 ? 3 : 2;
  // Three distinct radii, not two: `big:true/false` gave level 3 two identical
  // rings, so the one thing this game is filed under — size — was not on screen.
  return { type: 'drag-drop', allCorrect: true, voice: 'stack_ring', voiceAlt: 'sort',
    items: Array.from({ length: n }, (_, i) => dragItem({ kind: 'ring', color: cs[i], step: i, id: `ring-${i}` }, 'pole')),
    targets: [{ id: 'pole', visual: { kind: 'pole', id: 'pole' } }] };
}

// D7 · Pure motor practice. No wrong answer anywhere.
function dragPathRound(level) {
  return { type: 'drag-drop', allCorrect: true, needed: 1,
    voice: 'walk_piko', voiceAlt: 'lets_play',
    items: [dragItem(PIKO, 'star')],
    targets: [{ id: 'star', visual: shape('star', 'yellow', 'star-goal') }] };
}

// D7 · Repetition, aim, hand-eye. Also no wrong answer.
function fruitPickRound(level) {
  const cs = pick(['red', 'orange', 'lime', 'gold'], level === 1 ? 2 : 3);
  return { type: 'drag-drop', allCorrect: true, voice: 'pick_fruit', voiceAlt: 'basket',
    items: cs.map((c, i) => dragItem({ kind: 'fruit', color: c, id: `fruit-${i}` }, 'basket')),
    targets: [{ id: 'basket', visual: { kind: 'basket', color: 'yellow', id: 'basket' } }] };
}

// D8 · An object onto its own shadow. Identity without colour or detail.
function tidyUpRound(level) {
  const cs = pick(['cup', 'shoe', 'spoon', 'ball'], level === 1 ? 2 : 3);
  const carried = level === 1 ? cs.slice(0, 1) : cs.slice(0, 2);
  return { type: 'drag-drop', voice: 'tidy_up', voiceAlt: 'sort',
    items: shuffle(carried.map((o) => dragItem({ ...img(o), id: o }, o))),
    targets: shuffle(cs.map((o) => ({ id: o, visual: { kind: 'shadow', id: `sh-${o}`, of: img(o) } }))) };
}

// D8 · What a thing is FOR. Reuses the already-recorded USE_SCENARIOS lines,
// so this game needs no new voice at all.
function givePikoRound(level) {
  const s = pick(USE_SCENARIOS, 1)[0];
  const others = s.others.slice(0, level === 1 ? 1 : 2);
  return { type: 'drag-drop', needed: 1, voice: s.voice,
    items: shuffle([s.correct, ...others].map((o) => dragItem({ ...img(o), id: o }, o === s.correct ? 'piko' : 'none'))),
    targets: [{ id: 'piko', visual: PIKO }] };
}

export const GAMES = [
  // ── Age-2 catalogue (active journey) ──
  // Order inside a world IS the difficulty ramp: worldGames() preserves this
  // array order and starsNeeded() gates on the index within the world.
  // ── DUNIA 1 · Bentuk (free) ── match by sight → sorter → by name → in objects
  { id: 'shape_same', world: 'shapes', free: true, skill: 'shapes', icon: 'Copy', name: ['Bentuk Sama', 'Match the Shape'], gen: shapeSameRound },
  { id: 'shape_hole', world: 'shapes', free: true, skill: 'shapes', icon: 'PuzzleIcon', name: ['Lubang Bentuk', 'Shape Hole'], gen: shapeHoleRound },
  { id: 'tap_shape', world: 'shapes', free: true, skill: 'shapes', icon: 'Shapes', name: ['Tekan Bentuk', 'Tap the Shape'], gen: tapShapeRound },
  { id: 'shape_find', world: 'shapes', free: true, skill: 'shapes', icon: 'CircleDot', name: ['Cari Yang Bulat', 'Find the Round One'], gen: shapeFindRound },
  { id: 'shape_post', world: 'shapes', free: true, skill: 'shapes', icon: 'Mailbox', name: ['Masukkan Bentuk', 'Post the Shape'], gen: shapePostRound },
  { id: 'shape_shadow', world: 'shapes', free: true, skill: 'shapes', icon: 'Moon', name: ['Cari Bayang', 'Find the Shadow'], gen: shapeShadowRound },
  // ── DUNIA 2 · Haiwan (free) ── no wrong answer → name → baby → home
  { id: 'touch_animal', world: 'animals', free: true, skill: 'animals', icon: 'Hand', name: ['Sentuh Haiwan', 'Touch the Animal'], gen: touchAnimalRound },
  { id: 'animal_name', world: 'animals', free: true, skill: 'animals', icon: 'Cat', name: ['Nama Haiwan', 'Animal Names'], gen: animalNameRound },
  { id: 'animal_baby', world: 'animals', free: true, skill: 'animals', icon: 'Egg', name: ['Anak Haiwan', 'Animal Babies'], gen: animalBabyRound },
  { id: 'animal_home', world: 'animals', free: true, skill: 'animals', icon: 'Home', name: ['Rumah Haiwan', 'Animal Homes'], gen: animalHomeRound },
  { id: 'animal_feed', world: 'animals', free: true, skill: 'animals', icon: 'Carrot', name: ['Beri Makan', 'Feed the Animals'], gen: animalFeedRound },
  { id: 'animal_home_drag', world: 'animals', free: true, skill: 'animals', icon: 'Bird', name: ['Hantar Balik', 'Take Them Home'], gen: animalHomeDragRound },
  // ── DUNIA 3 · Warna (Pro) ──
  { id: 'tap_colour', world: 'garden', skill: 'colours', icon: 'Palette', name: ['Tekan Warna', 'Tap the Colour'], gen: tapColourRound },
  { id: 'colour_match', world: 'garden', skill: 'colours', icon: 'Flower2', name: ['Padan Warna', 'Match the Colour'], gen: colourMatchRound },
  { id: 'colour_feed', world: 'garden', skill: 'colours', icon: 'Apple', name: ['Buah Untuk Piko', 'Feed Piko a Fruit'], gen: colourFeedRound },
  { id: 'colour_basket', world: 'garden', skill: 'colours', icon: 'ShoppingBasket', name: ['Bakul Warna', 'Colour Baskets'], gen: colourBasketRound },
  { id: 'colour_paint', world: 'garden', skill: 'colours', icon: 'Paintbrush', name: ['Padan Cat', 'Match the Paint'], gen: colourPaintRound },
  // ── DUNIA 4 · Bunyi (Pro) ──
  { id: 'animal_sound', world: 'forest', skill: 'listening', icon: 'PawPrint', name: ['Bunyi Haiwan', 'Animal Sound Match'], gen: animalSoundRound },
  { id: 'object_sound', world: 'forest', skill: 'listening', icon: 'Bell', name: ['Bunyi Apa Itu?', 'What Made That Sound?'], gen: objectSoundRound },
  { id: 'loud_soft', world: 'forest', skill: 'listening', icon: 'Volume2', name: ['Kuat atau Perlahan?', 'Loud or Soft?'], gen: loudSoftRound },
  { id: 'fast_slow', world: 'forest', skill: 'listening', icon: 'Rabbit', name: ['Laju atau Perlahan?', 'Fast or Slow?'], gen: fastSlowRound },
  { id: 'sound_drag', world: 'forest', skill: 'listening', icon: 'Mic', name: ['Bunyi Siapa?', 'Whose Sound?'], gen: soundDragRound },
  { id: 'music_maker', world: 'forest', skill: 'listening', icon: 'Music2', name: ['Muzik Piko', "Piko's Band"], gen: musicMakerRound },
  // ── DUNIA 5 · Ingatan (Pro) ── face-up match first, then hidden
  { id: 'same_picture', world: 'island', skill: 'memory', icon: 'Images', name: ['Gambar Sama', 'Same Picture'], gen: samePictureRound },
  { id: 'pair', world: 'island', skill: 'memory', icon: 'Copy', name: ['Cari Pasangan', 'Find the Pair'], gen: pairRound },
  { id: 'disappeared', world: 'island', skill: 'memory', icon: 'HelpCircle', name: ['Apa Yang Hilang?', 'What Disappeared?'], gen: disappearedRound },
  { id: 'location', world: 'island', skill: 'memory', icon: 'MapPin', name: ['Ingat Lokasi', 'Remember the Location'], gen: locationRound },
  { id: 'bring_back', world: 'island', skill: 'memory', icon: 'Magnet', name: ['Bawa Balik', 'Bring It Back'], gen: bringBackRound },
  { id: 'pack_bag', world: 'island', skill: 'memory', icon: 'Backpack', name: ['Isi Beg Piko', 'Pack the Bag'], gen: packBagRound },
  // ── DUNIA 6 · Saiz (Pro) ──
  { id: 'big_small', world: 'size', skill: 'size', icon: 'Maximize2', name: ['Besar atau Kecil?', 'Big or Small?'], gen: bigSmallRound },
  { id: 'long_short', world: 'size', skill: 'size', icon: 'Minus', name: ['Panjang atau Pendek?', 'Long or Short?'], gen: longShortRound },
  { id: 'tall_short', world: 'size', skill: 'size', icon: 'TreePine', name: ['Tinggi atau Rendah?', 'Tall or Short?'], gen: tallShortRound },
  { id: 'many_few', world: 'size', skill: 'size', icon: 'Grape', name: ['Banyak atau Sedikit?', 'Many or Few?'], gen: manyFewRound },
  { id: 'size_nest', world: 'size', skill: 'size', icon: 'CircleDashed', name: ['Lubang Besar, Lubang Kecil', 'Big Hole, Small Hole'], gen: sizeNestRound },
  { id: 'stack_ring', world: 'size', skill: 'size', icon: 'Donut', name: ['Susun Gelang', 'Stack the Rings'], gen: stackRingRound },
  // ── DUNIA 7 · Gerak (Pro) ── no wrong answer first
  { id: 'piko_piano', world: 'move', skill: 'coordination', icon: 'Music', name: ['Piano Piko', "Piko's Piano"], gen: pikoPianoRound },
  { id: 'pop_bubble', world: 'move', skill: 'coordination', icon: 'Circle', name: ['Pecahkan Buih', 'Pop the Bubbles'], gen: motionRound('bubble') },
  { id: 'tap_dots', world: 'move', skill: 'coordination', icon: 'Lightbulb', name: ['Lampu Menyala', 'Glowing Lights'], gen: motionRound('dots') },
  { id: 'catch_star', world: 'move', skill: 'coordination', icon: 'Star', name: ['Tangkap Bintang', 'Catch the Star'], gen: motionRound('star') },
  { id: 'drag_path', world: 'move', skill: 'coordination', icon: 'Route', name: ['Bawa Piko', 'Follow the Path'], gen: dragPathRound },
  { id: 'fruit_pick', world: 'move', skill: 'coordination', icon: 'Cherry', name: ['Kutip Buah', 'Pick the Fruit'], gen: fruitPickRound },
  { id: 'object_name', world: 'daily', skill: 'vocabulary', icon: 'Ear', name: ['Nama Benda', 'Object Names'], gen: objectNameRound },
  { id: 'play_together', world: 'daily', skill: 'vocabulary', icon: 'Users', name: ['Main Bersama', 'Play Together'], coplay: true, gen: playTogetherRound },
  { id: 'object_use', world: 'daily', skill: 'vocabulary', icon: 'CupSoda', name: ['Piko Perlukan Apa?', 'What Does Piko Need?'], gen: objectUseRound },
  { id: 'body_part', world: 'daily', skill: 'vocabulary', icon: 'Smile', name: ['Anggota Badan', 'Body Parts'], gen: bodyPartRound },
  { id: 'tidy_up', world: 'daily', skill: 'vocabulary', icon: 'Sparkle', name: ['Kemas Barang', 'Tidy Up'], gen: tidyUpRound },
  { id: 'give_piko', world: 'daily', skill: 'vocabulary', icon: 'HandHeart', name: ['Beri Pada Piko', 'Give It to Piko'], gen: givePikoRound },
  // ── Age 4+ shelf (kept but OUT of the age-2 journey; fate decided later) ──
  { id: 'baskets', age4: true, world: 'garden', skill: 'matching', icon: 'ShoppingBasket', name: ['Bakul Warna', 'Colour Baskets'], gen: basketsRound },
  { id: 'odd_one', age4: true, world: 'garden', skill: 'attention', icon: 'Eye', name: ['Cari Warna Lain', 'Find the Different Colour'], gen: oddRound },
  { id: 'colour_trail', age4: true, world: 'garden', skill: 'sequencing', icon: 'Footprints', name: ['Jejak Warna', 'Colour Trail'], gen: colourTrailRound },
  { id: 'pattern', age4: true, world: 'garden', skill: 'reasoning', icon: 'Grid3x3', name: ['Taman Corak', 'Pattern Garden'], gen: patternRound },
  { id: 'detective', age4: true, world: 'garden', skill: 'reasoning', icon: 'Search', name: ['Detektif Warna & Bentuk', 'Colour & Shape Detective'], gen: detectiveRound },
  { id: 'sort_world', age4: true, world: 'garden', skill: 'categorising', icon: 'LayoutGrid', name: ['Susun Dunia', 'Sort the World'], gen: sortRound },
  { id: 'listen_choose', age4: true, world: 'forest', skill: 'vocabulary', icon: 'Ear', name: ['Dengar & Pilih', 'Listen and Choose'], gen: listenRound },
  { id: 'sound_seq', age4: true, world: 'forest', skill: 'sequencing', icon: 'Music', name: ['Urutan Bunyi', 'Sound Sequence'], gen: soundSeqRound },
  { id: 'piko_says', age4: true, world: 'forest', skill: 'movement', icon: 'PersonStanding', name: ['Piko Kata', 'Piko Says'], gen: pikoSaysRound },
  { id: 'two_step', age4: true, world: 'forest', skill: 'movement', icon: 'ListChecks', name: ['Misi Dua Langkah', "Piko's Two-Step Mission"], gen: twoStepRound },
  { id: 'sound_master', age4: true, world: 'forest', skill: 'listening', icon: 'Headphones', name: ['Pakar Bunyi', 'Sound Master'], gen: soundMasterRound },
  { id: 'echo_seq', age4: true, world: 'forest', skill: 'sequencing', icon: 'Waves', name: ['Gema Gua', 'Cave Echo'], gen: echoSeqRound },
  { id: 'copy_piko', age4: true, world: 'island', skill: 'sequencing', icon: 'Repeat', name: ['Ikut Piko', "Copy Piko's Sequence"], gen: copyPikoRound },
  { id: 'magic_bag', age4: true, world: 'island', skill: 'memory', icon: 'Briefcase', name: ['Beg Ajaib Piko', "Piko's Magic Bag"], gen: bagRound },
  { id: 'pattern_pro', age4: true, world: 'island', skill: 'reasoning', icon: 'Sparkles', name: ['Corak Diraja', 'Royal Patterns'], gen: patternProRound },
  { id: 'super_pair', age4: true, world: 'island', skill: 'memory', icon: 'Layers', name: ['Pasangan Super', 'Super Pairs'], gen: superPairRound },
  { id: 'mega_trail', age4: true, world: 'island', skill: 'sequencing', icon: 'Route', name: ['Jejak Mega', 'Mega Trail'], gen: megaTrailRound },
];

export const ACTIVE_GAMES = GAMES.filter((g) => !g.age4);
export const FREE_GAME_IDS = ACTIVE_GAMES.filter((g) => g.free).map((g) => g.id);
export const getGame = (id) => GAMES.find((g) => g.id === id);
export const gameName = (g, en) => g.name[en ? 1 : 0];
// Every active game now has its own artwork, so the world-background fallback
// and the alias table are both gone. `colour_feed` used to alias to
// `bg_tap_colour`, which made two different games pixel-identical on the stage.
//
// If a game is ever added without art, add the file — do not reintroduce a
// fallback. Two games sharing one background is how the map became unreadable:
// the old convention was an empty scenic backdrop, and at 92px an empty meadow
// and an empty forest path are the same green smudge.
export const gameBg = (g) => `/assets/backgrounds/games/bg_${g.id}.webp`;

// WORLDS is the single source of truth — regions & counts derived at runtime (Phase 1/3).
export const worldGames = (wid) => ACTIVE_GAMES.filter((g) => g.world === wid).map((g) => g.id);
export const isWorldEmpty = (wid) => worldGames(wid).length === 0;
export const REGIONS = Object.values(WORLDS).map((w) => ({ id: w.id, games: worldGames(w.id) }));
export const PLAYABLE_REGIONS = REGIONS.filter((r) => r.games.length > 0);
export const proWorldsWithGames = () => Object.values(WORLDS).filter((w) => !w.free && !isWorldEmpty(w.id));
export const proGameCount = () => ACTIVE_GAMES.filter((g) => !g.free).length;

// First open, not-yet-completed game in journey order (falls back to last open).
export function currentJourney(entitlement, stars, gameStats = {}) {
  let lastOpen = null;
  for (let wi = 0; wi < PLAYABLE_REGIONS.length; wi++) {
    const r = PLAYABLE_REGIONS[wi];
    for (let gi = 0; gi < r.games.length; gi++) {
      const gid = r.games[gi];
      if (gameLockState(entitlement, gid, stars) !== 'open') continue;
      lastOpen = { gameId: gid, worldIndex: wi + 1, gameIndex: gi + 1, worldTotal: r.games.length, regionId: r.id };
      if (!gameStats[gid]?.completed) return lastOpen;
    }
  }
  return lastOpen;
}

export function starsNeeded(gameId) {
  for (const r of REGIONS) {
    const i = r.games.indexOf(gameId);
    // One star per step. A game awards up to ROUNDS (2) stars, so this always
    // leaves slack — a 2-year-old must never be blocked by an unreachable gate.
    if (i >= 0) return i;
  }
  return 0;
}

export function gameLockState(entitlement, gameId, stars) {
  const g = getGame(gameId);
  if (!g) return 'hidden';
  if (!g.free && entitlement?.plan !== 'PRO') return 'pro';
  if (stars < starsNeeded(gameId)) return 'stars';
  return 'open';
}

export function canPlayGame(entitlement, gameId, stars = Infinity) {
  return gameLockState(entitlement, gameId, stars) === 'open';
}

// Next game in journey order that the child can actually open right now.
// Returns null at the end of the journey or at a locked boundary.
export function nextPlayableGame(gameId, entitlement, stars) {
  const order = PLAYABLE_REGIONS.flatMap((r) => r.games);
  const i = order.indexOf(gameId);
  if (i === -1) return null;
  for (let j = i + 1; j < order.length; j++) {
    if (canPlayGame(entitlement, order[j], stars)) return order[j];
  }
  return null;
}

export const SKILL_LABELS = {
  colours: ['Warna', 'Colours'], matching: ['Padanan', 'Matching'], attention: ['Tumpuan', 'Attention'],
  listening: ['Mendengar', 'Listening'], vocabulary: ['Perbendaharaan kata', 'Vocabulary'], memory: ['Ingatan', 'Memory'],
  sequencing: ['Urutan', 'Sequencing'], reasoning: ['Penaakulan', 'Reasoning'], movement: ['Pergerakan', 'Movement'],
  categorising: ['Pengelasan', 'Categorising'], size: ['Saiz', 'Size'], shapes: ['Bentuk', 'Shapes'],
  animals: ['Haiwan', 'Animals'], coordination: ['Koordinasi', 'Coordination'],
};
